import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Field, Input, Modal, Select, Spinner, useToast } from "@aigntx/ui";
import { endpoints } from "../api";
import { FormFieldGrid, missingRequired } from "../components/FormFields";
import type { FormValues } from "../components/FormFields";
import type {
  ProjectDetail,
  RegisterItem,
  RegisterItemDetail,
  RegisterKind,
  Transmittal,
} from "../types";

/**
 * The project registers — drawings, specifications, design deliverables,
 * authority submissions and snags.
 *
 * A register is not a list of forms. An item stays on it for the life of the
 * project and gains revisions, and the previous revision is never overwritten:
 * that is what makes the register answer "what was issued for construction on
 * that date" two years later.
 */
export function RegistersPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [kinds, setKinds] = useState<RegisterKind[]>([]);
  const [items, setItems] = useState<RegisterItem[] | null>(null);
  const [open, setOpen] = useState<RegisterItemDetail | null>(null);
  const [adding, setAdding] = useState(false);
  const [transmitting, setTransmitting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [sent, setSent] = useState<Transmittal[]>([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const kindCode = params.get("kind") || "";
  const kind = useMemo(() => kinds.find((k) => k.code === kindCode) ?? kinds[0], [kinds, kindCode]);

  useEffect(() => {
    Promise.all([endpoints.getProject(id), endpoints.registersCatalog(), endpoints.listTransmittals(id)])
      .then(([p, c, tr]) => {
        setProject(p);
        // Only the registers that belong to where the project actually is: a
        // project in construction should not be cluttered with the design
        // deliverables schedule it finished a year ago.
        setKinds(c.filter((k) => k.stage === "both" || k.stage === p.stage));
        setSent(tr);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);

  const load = useCallback(() => {
    if (!kind) return;
    setItems(null);
    endpoints
      .listRegister(id, kind.code)
      .then(setItems)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [id, kind]);

  useEffect(() => {
    load();
    setPicked(new Set());
  }, [load]);

  const canEdit = project?.myAbilities.includes("write") ?? false;

  const shown = useMemo(() => {
    const list = items ?? [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(
      (i) =>
        i.number.toLowerCase().includes(q) ||
        i.title.toLowerCase().includes(q) ||
        Object.values(i.data ?? {}).some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [items, search]);

  function togglePick(itemId: string) {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  if (error)
    return (
      <div className="page">
        <Card>{error}</Card>
      </div>
    );
  if (!project || !kind)
    return (
      <div className="page center">
        <Spinner />
      </div>
    );

  return (
    <div className="page">
      <div className="crumb">
        <Link to="/projects">{t("projects.title")}</Link> <span className="muted">/ </span>
        <Link to={`/projects/${id}`}>{project.name}</Link>{" "}
        <span className="muted">/ {t("reg.title")}</span>
      </div>

      <header className="page__head between">
        <div>
          <h1 className="page__title">{kind.title}</h1>
          <p className="page__sub">{kind.summary}</p>
        </div>
        <div className="head-actions">
          {picked.size > 0 && (
            <Button size="sm" variant="outline" onClick={() => setTransmitting(true)}>
              {t("reg.transmitN", { count: picked.size })}
            </Button>
          )}
          {canEdit && (
            <Button size="sm" onClick={() => setAdding(true)}>
              {t("reg.add", { what: kind.singular })}
            </Button>
          )}
        </div>
      </header>

      <div className="segmented reg-tabs">
        {kinds.map((k) => (
          <button
            key={k.code}
            type="button"
            className={k.code === kind.code ? "seg is-on" : "seg"}
            onClick={() => setParams({ kind: k.code })}
          >
            {k.title}
          </button>
        ))}
      </div>

      <Card>
        <div className="tab-toolbar">
          <Input
            placeholder={t("reg.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <span className="muted">{t("reg.count", { count: shown.length })}</span>
        </div>

        {items === null ? (
          <div className="center">
            <Spinner />
          </div>
        ) : shown.length === 0 ? (
          <p className="muted">{t("reg.empty", { what: kind.title.toLowerCase() })}</p>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  {canEdit && <th aria-label="select" />}
                  <th>{t("reg.number")}</th>
                  <th>{t("reg.itemTitle")}</th>
                  <th>{t("reg.rev")}</th>
                  <th>{t("forms.state")}</th>
                  <th>{t("reg.responsible")}</th>
                  <th>{t("reg.due")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((it) => (
                  <tr key={it.id}>
                    {canEdit && (
                      <td>
                        <input
                          type="checkbox"
                          checked={picked.has(it.id)}
                          onChange={() => togglePick(it.id)}
                          aria-label={`Select ${it.number}`}
                        />
                      </td>
                    )}
                    <td>
                      <button
                        className="mono form-no linkish"
                        onClick={() =>
                          void endpoints.getRegisterItem(id, kind.code, it.id).then(setOpen)
                        }
                      >
                        {it.number}
                      </button>
                    </td>
                    <td>
                      {it.title}
                      <div className="u-mail">
                        {kind.fields
                          .filter((f) => f.type === "select" && it.data?.[f.key])
                          .map((f) => String(it.data[f.key]))
                          .join(" · ")}
                      </div>
                    </td>
                    <td>
                      {it.currentRev ? (
                        <Badge tone="accent">{it.currentRev}</Badge>
                      ) : (
                        <span className="muted">{t("reg.noRev")}</span>
                      )}
                    </td>
                    <td>
                      <Badge tone={statusTone(it.status)}>{it.status || "—"}</Badge>
                    </td>
                    <td>{it.responsible || "—"}</td>
                    <td className="mono">
                      {it.dueDate || "—"}
                      {it.overdue && <Badge tone="danger">{t("forms.overdue")}</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {sent.length > 0 && (
        <Card title={t("reg.transmittals")} className="grid__wide">
          <p className="page__sub">{t("reg.transmittalsHint")}</p>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("reg.number")}</th>
                  <th>{t("forms.subject")}</th>
                  <th>{t("reg.to")}</th>
                  <th>{t("reg.contents")}</th>
                  <th>{t("sup.when")}</th>
                </tr>
              </thead>
              <tbody>
                {sent.map((tr) => (
                  <tr key={tr.id}>
                    <td className="mono">{tr.number}</td>
                    <td>{tr.subject}</td>
                    <td>
                      {tr.toName || tr.toPartyLabel || "—"}
                      {tr.toName && tr.toPartyLabel && <div className="u-mail">{tr.toPartyLabel}</div>}
                    </td>
                    {/* the revision AS SENT — this is the whole point of a transmittal */}
                    <td>
                      {tr.items.map((i) => (
                        <span key={i.itemId} className="chip-static mono">
                          {i.number} {i.rev && `· ${i.rev}`}
                        </span>
                      ))}
                    </td>
                    <td className="mono">{tr.createdAt.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {adding && (
        <AddItemModal
          projectId={id}
          kind={kind}
          onClose={() => setAdding(false)}
          onAdded={(it) => {
            setAdding(false);
            setOpen(it);
            load();
            toast.success(t("reg.added", { number: it.number }));
          }}
        />
      )}
      {open && (
        <ItemModal
          projectId={id}
          kind={kind}
          item={open}
          onClose={() => {
            setOpen(null);
            load();
          }}
          onChanged={setOpen}
        />
      )}
      {transmitting && (
        <TransmitModal
          projectId={id}
          items={(items ?? []).filter((i) => picked.has(i.id))}
          onClose={() => setTransmitting(false)}
          onSent={(tr) => {
            setTransmitting(false);
            setPicked(new Set());
            setSent((s) => [tr, ...s]);
            toast.success(t("reg.transmitted", { number: tr.number }));
          }}
        />
      )}
    </div>
  );
}

function statusTone(s: string): "good" | "warn" | "danger" | "accent" | "neutral" {
  if (["Issued", "Approved", "Closed"].includes(s)) return "good";
  if (["Superseded", "Void", "Rejected"].includes(s)) return "neutral";
  if (["Comments received", "Ready for check", "On hold"].includes(s)) return "warn";
  if (s === "Open") return "danger";
  return "accent";
}

/* ---- add ---- */

function AddItemModal({
  projectId,
  kind,
  onClose,
  onAdded,
}: {
  projectId: string;
  kind: RegisterKind;
  onClose: () => void;
  onAdded: (i: RegisterItemDetail) => void;
}) {
  const { t } = useTranslation();
  const [number, setNumber] = useState("");
  const [title, setTitle] = useState("");
  const [responsible, setResponsible] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [values, setValues] = useState<FormValues>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    const missing = missingRequired(kind.fields, values);
    if (missing.length) {
      setError(t("forms.required", { fields: missing.join(", ") }));
      return;
    }
    setBusy(true);
    setError("");
    try {
      onAdded(
        await endpoints.createRegisterItem(projectId, kind.code, {
          number, title, responsible, dueDate, data: values,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t("reg.add", { what: kind.singular })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("common.create")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}
      {kind.numbering === "manual" ? (
        <Field label={t("reg.number")} hint={t("reg.numberHint")}>
          <Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="AR-101" />
        </Field>
      ) : (
        <p className="muted">{t("reg.autoNumber", { code: kind.code })}</p>
      )}
      <Field label={t("reg.itemTitle")}>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <FormFieldGrid
        fields={kind.fields}
        values={values}
        onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
      />
      <Field label={t("reg.responsible")} hint={t("reg.responsibleHint")}>
        <Input value={responsible} onChange={(e) => setResponsible(e.target.value)} />
      </Field>
      <Field label={t("reg.due")}>
        <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
      </Field>
    </Modal>
  );
}

/* ---- item + its revision history ---- */

function ItemModal({
  projectId,
  kind,
  item,
  onClose,
  onChanged,
}: {
  projectId: string;
  kind: RegisterKind;
  item: RegisterItemDetail;
  onClose: () => void;
  onChanged: (i: RegisterItemDetail) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [issuing, setIssuing] = useState(false);
  const [values, setValues] = useState<FormValues>({});
  const [issuedOn, setIssuedOn] = useState(new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState(item.status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function issue() {
    const missing = missingRequired(kind.revFields, values);
    if (missing.length) {
      setError(t("forms.required", { fields: missing.join(", ") }));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const updated = await endpoints.addRevision(projectId, kind.code, item.id, {
        status, issuedOn, data: values,
      });
      onChanged(updated);
      setIssuing(false);
      setValues({});
      toast.success(t("reg.issued", { rev: updated.currentRev }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function attach(revId: string, file: File) {
    try {
      onChanged(await endpoints.uploadRevisionFile(projectId, kind.code, item.id, revId, file));
      toast.success(t("reg.fileAttached"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Modal
      open
      title={`${item.number} — ${item.title}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.close")}
          </Button>
          {item.canEdit && !issuing && (
            <Button onClick={() => setIssuing(true)}>{t("reg.issueNext")}</Button>
          )}
          {issuing && (
            <Button loading={busy} onClick={() => void issue()}>
              {t("reg.issue")}
            </Button>
          )}
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}

      <div className="kv">
        <span className="kv__k">{t("forms.state")}</span>
        <span className="kv__v">
          <Badge tone={statusTone(item.status)}>{item.status || "—"}</Badge>
        </span>
      </div>
      {kind.fields.map((f) =>
        item.data?.[f.key] ? (
          <div className="kv" key={f.key}>
            <span className="kv__k">{f.label}</span>
            <span className="kv__v">{String(item.data[f.key])}</span>
          </div>
        ) : null,
      )}

      {issuing && (
        <div className="reg-issue">
          <h4>{t("reg.issuingNext")}</h4>
          <p className="muted">{t("reg.issueHint")}</p>
          <Field label={t("reg.issuedOn")}>
            <Input type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
          </Field>
          <Field label={t("forms.state")}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              {kind.statuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
          <FormFieldGrid
            fields={kind.revFields}
            values={values}
            onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))}
          />
        </div>
      )}

      <h4 className="reg-h">{t("reg.history")}</h4>
      {item.revisions.length === 0 ? (
        <p className="muted">{t("reg.noHistory")}</p>
      ) : (
        <ol className="reg-revs">
          {item.revisions.map((r) => (
            <li key={r.id} className={r.superseded ? "reg-rev is-old" : "reg-rev"}>
              <div className="reg-rev__head">
                <Badge tone={r.superseded ? "neutral" : "good"}>{r.rev}</Badge>
                <span className="mono">{r.issuedOn}</span>
                {r.superseded && <span className="reg-sup">{t("reg.superseded")}</span>}
                <span className="muted">{r.createdByName}</span>
                {r.actingAdmin && <Badge tone="danger">via {r.actingAdmin}</Badge>}
              </div>
              {kind.revFields.map((f) =>
                r.data?.[f.key] ? (
                  <div className="reg-rev__f" key={f.key}>
                    <b>{f.label}:</b> {String(r.data[f.key])}
                  </div>
                ) : null,
              )}
              <div className="reg-rev__file">
                {r.hasFile ? (
                  <a
                    className="ax-btn ax-btn--outline ax-btn--sm"
                    href={endpoints.revisionFileUrl(projectId, kind.code, item.id, r.id)}
                  >
                    ⤓ {r.filename}
                  </a>
                ) : item.canEdit ? (
                  <label className="ax-btn ax-btn--ghost ax-btn--sm">
                    {t("reg.attachFile")}
                    <input
                      type="file"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void attach(r.id, f);
                      }}
                    />
                  </label>
                ) : (
                  <span className="muted">{t("reg.noFile")}</span>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}

/* ---- transmittal ---- */

function TransmitModal({
  projectId,
  items,
  onClose,
  onSent,
}: {
  projectId: string;
  items: RegisterItem[];
  onClose: () => void;
  onSent: (t: Transmittal) => void;
}) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState("");
  const [toParty, setToParty] = useState("contractor");
  const [toName, setToName] = useState("");
  const [purpose, setPurpose] = useState("Construction");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setBusy(true);
    setError("");
    try {
      onSent(
        await endpoints.createTransmittal(projectId, {
          subject, toParty, toName, purpose, notes, itemIds: items.map((i) => i.id),
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t("reg.newTransmittal")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("reg.record")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}
      <p className="muted">{t("reg.transmitHint")}</p>
      <div className="chips">
        {items.map((i) => (
          <span key={i.id} className="chip-static mono">
            {i.number} {i.currentRev && `· ${i.currentRev}`}
          </span>
        ))}
      </div>
      <Field label={t("forms.subject")}>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </Field>
      <Field label={t("reg.toParty")}>
        <Select value={toParty} onChange={(e) => setToParty(e.target.value)}>
          <option value="contractor">Contractor</option>
          <option value="client">Client / Owner</option>
          <option value="engineer">Engineer / Consultant</option>
          <option value="third_party">Third-party consultant</option>
          <option value="project_manager">Project Manager</option>
        </Select>
      </Field>
      <Field label={t("reg.toName")} hint={t("reg.toNameHint")}>
        <Input value={toName} onChange={(e) => setToName(e.target.value)} />
      </Field>
      <Field label={t("reg.purpose")}>
        <Select value={purpose} onChange={(e) => setPurpose(e.target.value)}>
          {["Information", "Review / Comment", "Approval", "Tender", "Construction", "Record"].map(
            (p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ),
          )}
        </Select>
      </Field>
      <Field label={t("reg.notes")}>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Modal>
  );
}
